import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { SITEWIDE_RESULT_ICON } from "@search/api/constants";
import { SitewideResultTypeEnum } from "@search/api/enums";
import type {
  ContentResult,
  PlaceResult,
  RegionResult,
  SitewideResult,
  VenueResult,
} from "@search/api/types";
import { useSitewideQuery } from "@sitewide/api/useSitewideQuery";
import clsx from "clsx";

import { Icon } from "@/booking/components/atoms/media/Icon";
import {
  Autocomplete,
  AutocompleteResultsType,
  AutocompleteResultType,
} from "@/components/compounds/forms/Autocomplete";
import type { CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./SitewideAutocomplete.module.css";

const ref = "sitewide-autocomplete";

interface SitewideAutocompleteProps extends Partial<CommonProps> {
  mobile?: boolean;
}

function render({ name, subtitle, type }: AutocompleteResultType) {
  const icon = type ? SITEWIDE_RESULT_ICON[type] : undefined;

  return (
    <div>
      <p>{name}</p>
      <p>{subtitle}</p>
      {icon && <Icon name={icon} />}
    </div>
  );
}

function transform(data: Array<SitewideResult>): AutocompleteResultsType {
  return data.map((result: any, index: number) => ({ id: index, ...result }));
}

function onSelect(item: SitewideResult) {
  const { type } = item;
  let href;

  if (type === SitewideResultTypeEnum.REGION) {
    const { id } = item as RegionResult;

    href = `/search?region_ids=${id}`;
  } else {
    const { slug } = item as ContentResult | PlaceResult | VenueResult;

    href =
      type === SitewideResultTypeEnum.PLACE
        ? `/search?location=${slug},50`
        : `/${slug}`;
  }

  window.location.href = href;
}

function useSitewideAutocomplete(props: SitewideAutocompleteProps) {
  const { id = createId(ref), mobile = false } = props;
  const className = clsx(styles[ref], mobile && styles.nav, props.className);
  const { searchSitewide } = useSitewideQuery();

  return {
    className,
    color: props.color,
    iconColor: props.iconColor,
    id,
    searchSitewide,
  };
}

function SitewideAutocomplete(props: SitewideAutocompleteProps) {
  const { className, color, iconColor, id, searchSitewide } =
    useSitewideAutocomplete(props);

  async function search(text: string) {
    if (text) {
      const response = await searchSitewide(text);

      if (!response) {
        throw new Error(`Failed to fetch: "${text}"`);
      }

      return response?.data;
    }
  }

  return (
    <Autocomplete
      className={className}
      color={color}
      icon={faMagnifyingGlass}
      iconColor={iconColor}
      id={id}
      minLength={3}
      noResultsText={"No locations found"}
      placeholder={"Search for a destination or hotel..."}
      render={render}
      search={search}
      size={10}
      transform={transform}
      onUpdate={onSelect}
    />
  );
}

export default SitewideAutocomplete;
export { SitewideAutocomplete, type SitewideAutocompleteProps };
